import type { Schedule } from "../interface/schedule"

export const MOSCOW_TIME_ZONE = "Europe/Moscow"
const localPattern =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(\.\d{1,3})?)?$/

function localDate(value: string): string {
  const parts = localPattern.exec(value)
  if (!parts) throw new Error("Введите дату МСК: ГГГГ-ММ-ДДTЧЧ:ММ")
  const [, year, month, day, hour, minute, second = "00", fraction = ""] = parts
  const y = Number(year),
    m = Number(month),
    d = Number(day)
  const leap = y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  if (
    y < 1 ||
    m < 1 ||
    m > 12 ||
    d < 1 ||
    d > days[m - 1] ||
    Number(hour) > 23 ||
    Number(minute) > 59 ||
    Number(second) > 59
  )
    throw new Error("Некорректная календарная дата или время МСК")
  const ms = Number(fraction.slice(1).padEnd(3, "0"))
  return `${year}-${month}-${day}T${hour}:${minute}:${second}${ms ? `.${String(ms).padStart(3, "0")}` : ""}`
}

/** Resolve a wall time against Intl's Moscow rules, never the browser timezone.
 * Nearby days expose both offsets at Moscow clock changes. Only a unique
 * round-trip is accepted; folds, gaps and historical second offsets are not guessed.
 * Modern dates still produce +03:00, independently of schedule rule.tz. */
export function parseMoscowInput(value: string): string | null {
  if (value === "") return null
  const local = localDate(value)
  const wallTime = instant(`${local}Z`).getTime()
  const offsets = new Set(
    [-86400000, 0, 86400000].map(
      (delta) =>
        formatter
          .formatToParts(new Date(wallTime + delta))
          .find((part) => part.type === "timeZoneName")?.value,
    ),
  )
  const candidates: string[] = []
  for (const name of offsets) {
    const offset =
      name === "GMT" ? "+00:00" : /^GMT([+-]\d{2}:\d{2})$/.exec(name ?? "")?.[1]
    // Do not silently choose the minute-offset side of a fold whose other side
    // has historical seconds (e.g. Moscow's July 1919 transition).
    if (!offset)
      throw new Error(
        "Неподдерживаемое историческое смещение МСК. Измените дату.",
      )
    const candidate = `${local}${offset}`
    if (formatMoscowInput(candidate) === local) candidates.push(candidate)
  }
  if (candidates.length !== 1)
    throw new Error(
      candidates.length
        ? "Неоднозначное историческое время МСК. Выберите время вне перевода часов."
        : "Время МСК не существует или историческое смещение не поддерживается. Измените дату.",
    )
  return candidates[0]
}

function instant(value: string): Date {
  if (typeof value !== "string")
    throw new Error("Ожидается дата с часовым поясом")
  const match = /^(.*)(Z|[+-]\d{2}:\d{2})$/.exec(value)
  if (!match) throw new Error("У даты отсутствует часовой пояс")
  const [, local, offset] = match
  const normalized = localDate(local)
  if (
    offset !== "Z" &&
    (Number(offset.slice(1, 3)) > 23 || Number(offset.slice(4)) > 59)
  )
    throw new Error("Некорректный часовой пояс")
  const result = new Date(`${normalized}${offset}`)
  if (!Number.isFinite(result.getTime())) throw new Error("Некорректная дата")
  return result
}

const formatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: MOSCOW_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
  timeZoneName: "longOffset",
})

/** legacyNaive is only for documented legacy schedule timestamps from reads,
 * never for validating a new wire request or arbitrary unzoned timestamps. */
export function formatMoscowInput(
  value: string | null | undefined,
  { legacyNaive = false }: { legacyNaive?: boolean } = {},
): string {
  if (value == null) return ""
  // This is already documented Moscow wall time, not a fixed-offset instant.
  if (legacyNaive && localPattern.test(value)) return localDate(value)
  const date = instant(value)
  const parts = Object.fromEntries(
    formatter.formatToParts(date).map(({ type, value }) => [type, value]),
  )
  const ms = date.getUTCMilliseconds()
  return `${parts.year.padStart(4, "0")}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}${ms ? `.${String(ms).padStart(3, "0")}` : ""}`
}

/** Validate only documented schedule semantics. TZ is opaque retained metadata,
 * not a replacement for the required numeric rule.tz. Future admission is server-owned. */
export function validateSchedule(schedule: Schedule): void {
  if (!schedule || !Array.isArray(schedule.schedules))
    throw new Error("Некорректное расписание")
  for (const rule of schedule.schedules) {
    if (!rule) throw new Error("Некорректное правило")
    if (rule.type === "interval") {
      const max = { m: 59, h: 23, d: 31 }[rule.period]
      if (
        !max ||
        !Number.isInteger(rule.value) ||
        rule.value < 1 ||
        rule.value > max
      )
        throw new Error(
          "Интервал: минуты 1–59, часы 1–23, дни 1–31; только целые числа",
        )
    } else {
      const valid = (values: number[], min: number, max: number) =>
        Array.isArray(values) &&
        new Set(values).size === values.length &&
        values.every(
          (value) => Number.isInteger(value) && value >= min && value <= max,
        )
      if (
        rule.type !== "schedule" ||
        typeof rule.tz !== "number" ||
        !Number.isFinite(rule.tz) ||
        !valid(rule.weekDays, 1, 7) ||
        !valid(rule.hour, 0, 23) ||
        !valid(rule.minute, 0, 59)
      )
        throw new Error(
          "Расписание: уникальные целые дни 1–7, часы 0–23, минуты 0–59 и числовой tz обязательны",
        )
    }
  }
  if (schedule.TZ === null || schedule.TZ instanceof Date)
    throw new Error("Некорректный TZ")
  const start =
    schedule.dateStart == null ? undefined : instant(schedule.dateStart)
  const end = schedule.dateEnd == null ? undefined : instant(schedule.dateEnd)
  if (start && end && end.getTime() < start.getTime())
    throw new Error("Дата окончания должна быть не раньше даты начала")
}
